import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SearchIcon from "@mui/icons-material/Search";
import DataTable from "../../pages/Requirement/components/common/ReusableTable";
import { fetchData } from "../../services/BackendServices";

const themeColor = "#de6b14";

const tradeColumns = [
  { id: "sourceCountry", label: "Source Country" },
  { id: "destinationCountry", label: "Destination Country" },
  { id: "product", label: "Product" },
  { id: "hsCode", label: "HS Code" },
  { id: "description", label: "Description" },
];

export default function MarketInsight() {
  const [activeTab, setActiveTab] = useState("trade-route");
  const [isLoading, setIsLoading] = useState(true);

  // Trade Route States - Persistent across tabs
  const [tradeRoutes, setTradeRoutes] = useState([]);
  const [selectedTradeRoute, setSelectedTradeRoute] = useState(null);

  const [sourceCountries, setSourceCountries] = useState([]);
  const [destinationCountries, setDestinationCountries] = useState([]);

  const [sourceCountry, setSourceCountry] = useState(null);
  const [destinationCountry, setDestinationCountry] = useState(null);
  const [showRouteTable, setShowRouteTable] = useState(false);

  // Product Tab States
  const [category, setCategory] = useState(null);
  const [product, setProduct] = useState(null);
  const [showProductTable, setShowProductTable] = useState(false);

  // Data
  const [marketItems, setMarketItems] = useState([]);
  const [filteredTradeData, setFilteredTradeData] = useState([]);
  const [filteredProductData, setFilteredProductData] = useState([]);

  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [continentToCountriesMap, setContinentToCountriesMap] = useState({});

  // TAB CHANGE - Preserves trade route selection
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    // Reset only tab-specific items (trade route stays!)
    if (newValue === "trade-route") {
      setShowProductTable(false);
      setCategory(null);
      setProduct(null);
    } else {
      setShowRouteTable(false);
      // Keep countries selected for quick return
    }
  };

  const fetchMarketData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchData({
        target: "listMarketTrades",
        userOrder: "Id",
        rowStart: 0,
        pageSize: 10000,
        filterTerms: {},
      });
  
      const items = response?.items || [];
      setMarketItems(items);
  
      // ==================== CONTINENT & ROUTES (unchanged) ====================
      const continentMap = {};
      const routeCountMap = {};
  
      items.forEach((item) => {
        const srcCont = item.source_continent?.trim();
        const destCont = item.destination_continent?.trim();
        const src = item.source;
        const dest = item.destination;
  
        if (!srcCont || !destCont || !src || !dest) return;
  
        const sourceCont = srcCont.trim();
        const destContNorm = destCont.trim();
  
        if (!continentMap[sourceCont]) continentMap[sourceCont] = new Set();
        if (!continentMap[destContNorm]) continentMap[destContNorm] = new Set();
        continentMap[sourceCont].add(src);
        continentMap[destContNorm].add(dest);
  
        const key = `${sourceCont}→${destContNorm}`;
        routeCountMap[key] = (routeCountMap[key] || 0) + 1;
      });
  
      Object.keys(continentMap).forEach((c) => {
        continentMap[c] = [...continentMap[c]].sort();
      });
      setContinentToCountriesMap(continentMap);
  
      const routes = Object.entries(routeCountMap)
        .map(([key, count]) => {
          const [source, destination] = key.split("→");
          return {
            label: `${source} → ${destination}`,
            // label: `${source} → ${destination} (${count.toLocaleString()} country pair${count > 1 ? "s" : ""})`,
            source,
            destination,
            count,
          };
        })
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  
      setTradeRoutes(routes);
  
      // ==================== CATEGORIES & PRODUCTS — FINAL, BULLETPROOF ====================
      const categoryToProducts = new Map(); // "Beverages/Crops" → Set of products
      const seenCategories = new Set();
  
      items.forEach((item) => {
        const rawCat = item.product_category;
        const product = item.product?.trim();
  
        if (!rawCat || typeof rawCat !== "string" || !product) return;
  
        const exactCat = rawCat.trim(); // ONLY TRIM — NOTHING ELSE
  
        // Deduplicate categories by exact string
        if (!seenCategories.has(exactCat)) {
          seenCategories.add(exactCat);
        }
  
        // Always collect products under this exact category
        if (!categoryToProducts.has(exactCat)) {
          categoryToProducts.set(exactCat, new Set());
        }
        categoryToProducts.get(exactCat).add(product);
      });
  
      // Build final sorted category list
      const finalCategories = Array.from(seenCategories).sort((a, b) => a.localeCompare(b));
      setCategories(finalCategories);
  
      // Build default product map
      const defaultProdMap = {};
      finalCategories.forEach((cat) => {
        const prods = categoryToProducts.get(cat) || new Set();
        defaultProdMap[cat] = [...prods].sort();
      });
      setProductsByCategory(defaultProdMap);
  
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update countries when route changes
  useEffect(() => {
    if (
      selectedTradeRoute &&
      continentToCountriesMap[selectedTradeRoute.source]
    ) {
      setSourceCountries(
        continentToCountriesMap[selectedTradeRoute.source] || []
      );
      setDestinationCountries(
        continentToCountriesMap[selectedTradeRoute.destination] || []
      );
    } else if (!selectedTradeRoute) {
      setSourceCountries([]);
      setDestinationCountries([]);
      setSourceCountry(null);
      setDestinationCountry(null);
    }
  }, [selectedTradeRoute, continentToCountriesMap]);
  


  // Toggle Direction
  const handleToggle = () => {
    if (!selectedTradeRoute || !sourceCountry || !destinationCountry) return;

    const reverseRoute = tradeRoutes.find(
      (r) =>
        r.source === selectedTradeRoute.destination &&
        r.destination === selectedTradeRoute.source
    );

    if (reverseRoute) {
      setSelectedTradeRoute(reverseRoute);
      setSourceCountry(destinationCountry);
      setDestinationCountry(sourceCountry);
    }
  };

  const handleTradeSearch = () => {
    if (!selectedTradeRoute || !sourceCountry || !destinationCountry) return;

    const filtered = marketItems.filter((item) => {
      const srcCont = item.source_continent?.trim();
      const destCont = item.destination_continent?.trim();
      const normSrc =
        srcCont?.charAt(0).toUpperCase() + srcCont?.slice(1).toLowerCase();
      const normDest =
        destCont?.charAt(0).toUpperCase() + destCont?.slice(1).toLowerCase();

      return (
        normSrc === selectedTradeRoute.source &&
        normDest === selectedTradeRoute.destination &&
        item.source === sourceCountry &&
        item.destination === destinationCountry
      );
    });

    const tableData = filtered.map((item) => ({
      sourceCountry: item.source,
      destinationCountry: item.destination,
      product: item.product,
      hsCode: item.hscode || "N/A",
      description: item.description,
    }));

    setFilteredTradeData(tableData);
    setShowRouteTable(true);
  };
  
// Update products when route changes — EXACT MATCH ONLY
useEffect(() => {
  if (activeTab !== "product") return;

  const update = () => {
    const map = {};

    categories.forEach((exactCategory) => {
      const prods = new Set();

      marketItems.forEach((item) => {
        const itemCat = item.product_category?.trim();
        const prod = item.product?.trim();
        if (!itemCat || !prod) return;

        // EXACT MATCH on category
        const catMatch = itemCat === exactCategory;

        let routeMatch = true;
        if (selectedTradeRoute) {
          const srcCont = item.source_continent?.trim();
          const destCont = item.destination_continent?.trim();
          if (!srcCont || !destCont) return;

          const normSrc = srcCont.trim();
          const normDest = destCont.trim();

          routeMatch = normSrc === selectedTradeRoute.source && normDest === selectedTradeRoute.destination;
        }

        if (catMatch && routeMatch) {
          prods.add(prod);
        }
      });

      map[exactCategory] = [...prods].sort();
    });

    setProductsByCategory(map);
  };

  update();
}, [selectedTradeRoute, activeTab, marketItems, categories]);


  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  return (
    <div className="py-4 card mt-2">
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h3"
          fontWeight="bold"
          color="#525252"
          mt={2}
          sx={{ fontSize: "30px !important" }}
        >
          Market Explorer:
        </Typography>
        <Typography
          variant="h4"
          fontWeight={500}
          color="#525252"
          sx={{ fontSize: "30px !important" }}
        >
          Your Gateway to Global Trade Opportunities
        </Typography>
        <Typography
          variant="body1"
          mt={3}
          className="lead col-lg-8 mx-auto"
          sx={{ fontSize: "18px !important", fontWeight: "400" }}
        >
          Identify products that move across Africa-Middle East routes
          <br />
          or find top sourcing and export markets for any agricultural
          commodity.
        </Typography>
      </Box>

      {/* TRADE CORRIDOR SELECTOR - Always Visible */}
      <Box sx={{ p: 4,  borderRadius: 2 }}>
        <div className="row g-4 justify-content-center">
          <div className="col-md-6">
            <Typography className="fw-semibold mb-2">
              Select Trade Corridor <span className="text-danger">*</span>
            </Typography>
            <Autocomplete
              options={tradeRoutes}
              getOptionLabel={(option) => option.label}
              value={selectedTradeRoute}
              onChange={(e, val) => setSelectedTradeRoute(val)}
              loading={isLoading}
              isOptionEqualToValue={(option, value) =>
                option.label === value?.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="--- Select Trade Corridor ---"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        </div>
      </Box>

      {/* TABS */}
      <Box>
        <Tabs
          value={activeTab}
          onChange={(e, v) => handleTabChange(v)}
          centered
          sx={{
            bgcolor: "white",
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <Tab
            label="Trade Route Explorer (Country Focus)"
            value="trade-route"
            sx={{
              fontSize: "13px !important",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor:
                activeTab === "trade-route" ? themeColor : "#f5f3f3",
              color: activeTab === "trade-route" ? "#fff !important" : "#555",
              "&:hover": {
                backgroundColor:
                  activeTab === "trade-route" ? themeColor : "#f0f0f0",
              },
            }}
          />
          <Tab
            label="Product Market Insights (Product Focus)"
            value="product"
            sx={{
              fontSize: "13px !important",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: activeTab === "product" ? themeColor : "#f5f3f3",
              color: activeTab === "product" ? "#fff !important" : "#555",
              "&:hover": {
                backgroundColor:
                  activeTab === "product" ? themeColor : "#f0f0f0",
              },
            }}
          />
        </Tabs>
      </Box>

      {/* FORM AREA */}
      <Box sx={{ p: 4, bgcolor: "#fafafa", borderRadius: 2, mt: 3 }}>
        {/* TRADE ROUTE TAB */}
        {activeTab === "trade-route" && (
          <div className="row g-4 align-items-end">
            <div className="col-md-5">
              <Typography className="fw-semibold mb-2">
                Sourcing Country <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={sourceCountries}
                value={sourceCountry}
                onChange={(e, val) => setSourceCountry(val)}
                disabled={!selectedTradeRoute}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedTradeRoute
                        ? `From ${selectedTradeRoute.source}...`
                        : "---Select Source---"
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </div>

            <div className="col-md-2 text-center">
              <Tooltip title="Swap direction">
                <IconButton
                  onClick={handleToggle}
                  disabled={
                    !selectedTradeRoute || !sourceCountry || !destinationCountry
                  }
                  sx={{
                    bgcolor: "#e8f5e8",
                    width: 56,
                    height: 56,
                    "&:hover": { bgcolor: "#c8e6c9" },
                    border: "2px solid #4caf50",
                  }}
                >
                  <SwapHorizIcon sx={{ fontSize: 32, color: "#2e7d32" }} />
                </IconButton>
              </Tooltip>
            </div>

            <div className="col-md-5">
              <Typography className="fw-semibold mb-2">
                Destination Country <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={destinationCountries}
                value={destinationCountry}
                onChange={(e, val) => setDestinationCountry(val)}
                disabled={!selectedTradeRoute}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedTradeRoute
                        ? `To ${selectedTradeRoute.destination}...`
                        : "---Select Destination---"
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </div>

            <div className="col-12 text-center mt-4">
              <Button
                variant="contained"
                className="submit-btn-more"
                size="large"
                startIcon={<SearchIcon />}
                onClick={() => {
                  if (!selectedTradeRoute || !sourceCountry || !destinationCountry) return;
                
                  console.log("DEBUG Trade Search →", {
                    route: selectedTradeRoute.label,
                    sourceCountry,
                    destinationCountry,
                  });
                
                  const filtered = marketItems.filter((item) => {
                    const itemSrcCont = item.source_continent?.trim();
                    const itemDestCont = item.destination_continent?.trim();
                
                    // EXACT MATCH — NO NORMALIZATION ANYMORE!
                    const routeMatch =
                      itemSrcCont === selectedTradeRoute.source &&
                      itemDestCont === selectedTradeRoute.destination;
                
                    const countryMatch =
                      (item.source === sourceCountry && item.destination === destinationCountry) ||
                      (item.source === destinationCountry && item.destination === sourceCountry);
                
                    const match = routeMatch && countryMatch;
                
                    if (match) {
                      console.log("MATCH:", item.source, "→", item.destination, item.product);
                    }
                
                    return match;
                  });
                
                  console.log(`Trade tab: Found ${filtered.length} records`);
                
                  const tableData = filtered.map((item) => ({
                    sourceCountry: item.source,
                    destinationCountry: item.destination,
                    product: item.product,
                    hsCode: item.hscode || "N/A",
                    description: item.description || "",
                  }));
                
                  setFilteredTradeData(tableData);
                  setShowRouteTable(true);
                }}
                disabled={
                  !selectedTradeRoute || !sourceCountry || !destinationCountry
                }
                sx={{
                  minWidth: 280,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  bgcolor: themeColor,
                  "&:hover": { bgcolor: "#c55f12" },
                  "&:disabled": { bgcolor: "#e0e0e0", color: "#aaa" },
                }}
              >
                Explore Trade Opportunities
              </Button>
            </div>
          </div>
        )}

        {/* PRODUCT TAB */}
        {activeTab === "product" && (
          <div className="row g-4">
            <div className="col-md-6">
              <Typography className="fw-semibold mb-2">
                Select Category <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                options={categories}
                value={category}
                onChange={(e, val) => {
                  setCategory(val);
                  setProduct(null);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="---Select Category---" />
                )}
              />
            </div>

            <div className="col-md-6">
              <Typography className="fw-semibold mb-2">
                Select Product <span className="text-danger">*</span>
              </Typography>
              <Autocomplete
                disabled={!category}
                options={category ? productsByCategory[category] || [] : []}
                value={product}
                onChange={(e, val) => setProduct(val)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="---Select Product---" />
                )}
              />
            </div>

            <div className="col-12 text-center mt-4">
              <Button
                variant="contained"
                className="submit-btn-more"
                size="large"
                startIcon={<SearchIcon />}
                onClick={() => {
                  if (!category || !product) return;
                
                  console.log("DEBUG → Selected Category:", category);
                  console.log("DEBUG → Selected Product:", product);
                  console.log("DEBUG → Selected Route:", selectedTradeRoute?.label);
                
                  const filtered = marketItems.filter((item) => {
                    // TRIM + EXACT MATCH for category and product
                    const itemCat = item.product_category?.trim();
                    const itemProd = item.product?.trim();
                
                    const catMatch = itemCat === category;  // Now matches even with old spaces
                    const prodMatch = itemProd === product;
                
                    // Optional: Respect selected trade route
                    let routeMatch = true;
                    if (selectedTradeRoute) {
                      const srcCont = item.source_continent?.trim();
                      const destCont = item.destination_continent?.trim();
                      routeMatch = srcCont === selectedTradeRoute.source && destCont === selectedTradeRoute.destination;
                    }
                
                    const match = catMatch && prodMatch && routeMatch;
                
                    if (match) {
                      console.log("MATCH FOUND:", {
                        source: item.source,
                        destination: item.destination,
                        category: itemCat,
                        product: itemProd,
                      });
                    }
                
                    return match;
                  });
                
                  console.log(`Found ${filtered.length} matching records`);
                
                  const tableData = filtered.map((item) => ({
                    sourceCountry: item.source,
                    destinationCountry: item.destination,
                    product: item.product?.trim(),
                    hsCode: item.hscode || "N/A",
                    description: item.description || "",
                  }));
                
                  setFilteredProductData(tableData);
                  setShowProductTable(true);
                  setShowRouteTable(false);
                }}
                disabled={!category || !product}
                sx={{
                  minWidth: 280,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  bgcolor: themeColor,
                  "&:hover": { bgcolor: "#c55f12" },
                  "&:disabled": { bgcolor: "#e0e0e0", color: "#aaa" },
                }}
              >
                Explore Market Opportunities
              </Button>
            </div>
          </div>
        )}
      </Box>

      {/* TABLES */}
      {activeTab === "trade-route" && showRouteTable && (
        <div className="mt-4">
          <DataTable
            columns={tradeColumns}
            data={filteredTradeData}
            title={`Trade Flow: ${sourceCountry} → ${destinationCountry}`}
            noDataMessage="No trade recorded in this direction."
          />
        </div>
      )}

      {activeTab === "product" && showProductTable && (
        <div className="mt-4">
          <DataTable
            columns={tradeColumns}
            data={filteredProductData}
            title={`Product Insights: ${product} (${category})`}
            noDataMessage={`No trade found for "${product}" in "${category}".`}
          />
        </div>
      )}
    </div>
  );
}
